import React, { useState, useEffect } from "react";
import { Search, Clock, Shield } from "lucide-react";
import { useBanRequests } from "../hooks/useBanRequests";
import { useCustomers } from "../hooks/useCustomers";
import { AllCustomersTable } from "../components/banRequest/AllCustomersTable";
import { BanRequestsTable } from "../components/banRequest/BanRequestTable";

export const BlacklistedPlayers: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const itemsPerPage = 25;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  const getCustomersQueryParams = () => {
    if (activeTab !== 0) return undefined;

    return {
      skip: (currentPage - 1) * itemsPerPage,
      limit: itemsPerPage,
      name: debouncedSearchTerm || undefined,
    };
  };

  const getBanRequestsQueryParams = () => {
    if (activeTab !== 1) return undefined;

    return {
      skip: (currentPage - 1) * itemsPerPage,
      limit: itemsPerPage,
      status: "pending" as const,
    };
  };

  const banRequestsQuery = useBanRequests(getBanRequestsQueryParams());
  const customersQuery = useCustomers(getCustomersQueryParams());

  const isLoading =
    activeTab === 0 ? customersQuery.isLoading : banRequestsQuery.isLoading;
  const isError =
    activeTab === 0 ? customersQuery.isError : banRequestsQuery.isError;
  const error = activeTab === 0 ? customersQuery.error : banRequestsQuery.error;

  const total =
    activeTab === 0
      ? customersQuery.data?.pagination?.total || 0
      : banRequestsQuery.data?.length || 0;

  const totalPages = Math.ceil(total / itemsPerPage);

  const tabs = [
    {
      id: 0,
      label: "All Customers",
      icon: <Shield className="w-4 h-4" />,
      color: "text-blue-400",
      description: "View all customers with their ban status",
    },
    {
      id: 1,
      label: "Pending Requests",
      icon: <Clock className="w-4 h-4" />,
      color: "text-orange-400",
      description: "Ban requests awaiting approval",
    },
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="bg-slate-800 rounded-lg p-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="ml-3 text-slate-400">Loading...</span>
          </div>
        </div>
      );
    }

    if (isError) {
      return (
        <div className="bg-slate-800 rounded-lg p-8">
          <div className="text-center text-red-400">
            Error loading data: {error?.message}
          </div>
        </div>
      );
    }

    if (activeTab === 0) {
      return (
        <AllCustomersTable
          customers={customersQuery.data?.items || []}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      );
    } else {
      return (
        <BanRequestsTable
          banRequests={banRequestsQuery.data || []}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
        />
      );
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Search Bar */}
        {activeTab === 0 && (
          <div className="bg-slate-800 rounded-lg p-4 mb-6">
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                size={20}
              />
              <input
                type="text"
                placeholder="Search customers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-slate-800 rounded-t-lg">
          <div className="flex border-b border-slate-700">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors relative ${
                  activeTab === tab.id
                    ? "text-white border-b-2 border-blue-500 bg-slate-700"
                    : "text-slate-400 hover:text-white hover:bg-slate-700"
                }`}
              >
                <span className={tab.color}>{tab.icon}</span>
                <span>{tab.label}</span>
                {/* Show count badge for pending requests */}
                {tab.id === 1 && banRequestsQuery.data?.length && (
                  <span className="ml-2 bg-orange-600 text-orange-100 text-xs px-2 py-1 rounded-full">
                    {banRequestsQuery.data.length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Description */}
        <div className="bg-slate-750 px-6 py-3 border-b border-slate-700">
          <p className="text-sm text-slate-300">
            {tabs[activeTab].description}
          </p>
        </div>

        {/* Content */}
        <div className="bg-slate-800 rounded-b-lg">{renderContent()}</div>

        {/* Stats */}
        {total > 0 && (
          <div className="mt-6 text-sm text-slate-400 text-center">
            {activeTab === 0 ? (
              <>
                Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                {Math.min(currentPage * itemsPerPage, total)} of {total} records
                {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
              </>
            ) : (
              `Showing ${total} pending requests`
            )}
          </div>
        )}
      </div>
    </div>
  );
};
